<template>
  <div
    class="homepage__chat cr-flex cr-align-center cr-justify-between cr-flex-col gap-5 cr-overflow-y-auto"
    :class="displayState === 'initial' ? 'spc-pt-3' : ''"
  >
    <template v-if="displayState === 'initial'">
      <div />
      <div class="cr-flex cr-flex-col gap-5 cr-w-100">
        <ClientHomePageGreeting :external-user-full-name="externalUserFullName" />
        <ClientHomePageStarterPrompts
          single-column
          @prompt-clicked="handlePromptClicked"
        />
      </div>
    </template>
    <div
      class="homepage__chat__content cr-align-center cr-w-100 cr-flex cr-flex-col cr-justify-between"
      :class="displayState === 'initial' ? '' : 'cr-h-100'"
    >
      <div
        v-if="!isScrolledToTop && threadId"
        class="homepage__chat__messages__top-fade-container cr-w-100"
      >
        <div class="homepage__chat__messages__top-fade cr-w-100" />
      </div>
      <div
        v-if="hasMessages"
        ref="messages"
        class="homepage__chat__messages cr-flex cr-flex-col cr-w-100 gap-5 spc-py-2 spc-mx-2"
      >
        <ClientHomePageChatMessage
          v-for="message in formattedMessagesWithContent"
          :key="message.id"
          assistant-chat-color="var(--grape-jelly-50)"
          :components="message.openai_message_components"
          :content="message.content"
          :external-user-full-name="externalUserFullName"
          :role="message.role"
        />
        <div
          v-if="isLoadingNextMessage"
          class="cr-flex cr-align-center gap-2"
        >
          <BaseIcon
            class="research-icon"
            color="var(--candy-wrapper-1000)"
            :font-size="'28px'"
            icon="fa-regular fa-loader"
            spin
          />
          <span class="research-label cr-font-poppins label-l-regular">
            Doing research
          </span>
        </div>
      </div>
      <CraftyCopilotChatMessageLoading
        v-else-if="isLoadingThread"
      />
      <div class="homepage__chat__search-bar-wrapper cr-w-100 cr-flex cr-align-end spc-pb-2 spc-pt-3">
        <ClientHomePageSearchBar
          v-if="!isLoadingThread"
          v-model="query"
          :class="displayState === 'chatting' ? 'search-bar--chatting' : ''"
          :search-placeholder="searchPlaceholder"
          theme="lightweight"
          @submit="handleSubmit"
          @update:model-value="query = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ClientHomeService } from '../services/api'
import ClientHomePageSearchBar from '../components/ClientHomePageSearchBar.vue'
import ClientHomePageChatMessage from '../components/ClientHomePageChatMessage.vue'
import BaseIcon from '../components/BaseIcon.vue'
import ClientHomePageStarterPrompts from './ClientHomePageStarterPrompts.vue'
import ClientHomePageGreeting from './ClientHomePageGreeting.vue'
import CraftyCopilotChatMessageLoading from './CraftyCopilotChatMessageLoading.vue'

const DISPLAY_STATES = {
  INITIAL: 'initial',
  CHATTING: 'chatting',
}

export default {
  name: 'CraftyCopilotChat',
  components: {
    BaseIcon,
    ClientHomePageSearchBar,
    ClientHomePageChatMessage,
    ClientHomePageStarterPrompts,
    ClientHomePageGreeting,
    CraftyCopilotChatMessageLoading,
  },
  inject: ['externalUserFullName'],
  props: {
    threadId: {
      type: [String, null],
      default: null,
    },
  },
  emits: ['chat-started'],
  data() {
    return {
      query: '',
      isLoadingNextMessage: false,
      isLoadingThread: false,
      errorMessage: null,
      response: null,
      messages: [],
      isScrolledToTop: true,
      newChatThreadId: null,
    }
  },
  computed: {
    formattedMessages() {
      return this.messages.map(message => ({
        ...message.attributes,
        openai_message_components: message.openai_message_components,
      }))
    },
    formattedMessagesWithContent() {
      return this.formattedMessages.filter(message => message.content)
    },
    mostRecentMessage() {
      return this.messages.at(-1)
    },
    mostRecentMessageComponents() {
      return this.mostRecentMessage?.openai_message_components
    },
    displayState() {
      if (this.hasMessages || this.threadId)
        return DISPLAY_STATES.CHATTING

      return DISPLAY_STATES.INITIAL
    },
    hasMessages() {
      return this.messages?.length > 0
    },
    messageQuantity() {
      return this.messages?.length
    },
    hasAssistantMessages() {
      return this.messages.some(message => message.attributes.role === 'assistant')
    },
    searchPlaceholder() {
      return this.hasAssistantMessages ? 'Ask a follow-up question' : 'Ask or search for anything'
    },
  },
  watch: {
    messageQuantity() {
      this.$nextTick(() => {
        this.scrollToBottomOfMessages()
      })
    },
    hasMessages(newVal) {
      if (!newVal)
        return

      this.$nextTick(() => {
        const messages = this.$refs['messages']
        if (!messages)
          return

        messages.addEventListener('scroll', this.handleScroll)
      })
    },
    errorMessage(newVal) {
      if (!newVal)
        return

      this.$toast.error(this.errorMessage)
    },
    threadId(_, oldVal) {
      if (!oldVal)
        return

      this.messages = []
    },
  },
  async beforeMount() {
    if (!this.threadId)
      return

    try {
      this.isLoadingThread = true
      this.messages = await ClientHomeService.fetchThread({ thread_id: this.threadId })
    } catch (error) {
      console.error('Error fetching thread:', error)
    } finally {
      this.isLoadingThread = false
    }
  },
  beforeUnmount() {
    this.$refs['messages']?.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async handleSubmit() {
      if (!this.query.trim()) {
        this.errorMessage = 'Please enter a valid query.'
        return
      }

      this.messages.push({
        attributes: {
          role: 'user',
          content: this.query,
        },
      })

      this.errorMessage = null
      this.response = null
      this.isLoadingNextMessage = true
      const currentQuery = this.query.slice()
      this.query = ''

      try {
        this.messages = await ClientHomeService.chat({ query: currentQuery, thread_id: this.threadId })
        this.newChatThreadId = this.messages[0].openai_message_thread.id
        this.$emit('chat-started', {
          threadId: this.newChatThreadId,
          title: this.messages[0].openai_message_thread.getRecord()?.subject,
        })
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || err.message || 'Something went wrong. Please try again.'
      } finally {
        this.isLoadingNextMessage = false
      }
    },
    handleScroll(el) {
      el = el.srcElement
      this.isScrolledToTop = el.scrollTop === 0
    },
    scrollToBottomOfMessages() {
      const messages = this.$refs['messages']
      if (!messages)
        return

      messages.scrollTop = messages.scrollHeight
    },
    handlePromptClicked(prompt) {
      this.query = prompt
      this.handleSubmit()
    },
  },
}
</script>

<style lang="scss" scoped>
.base-button {
  --button-primary-color: var(--grape-jelly-1000);
  --button-secondary-color: var(--salt);
}

.homepage {
  &__chat {
    display: flex;
    width: 100%;
    height: 100%;
    max-width: 100%;

    &__content {
    position: sticky;
    bottom: 0;
    }
    
    &__search-bar-wrapper {
      background-color: var(--salt);
    }

    &__messages {
      scroll-behavior: smooth;
      overflow-y: auto;

      &__top-fade-container {
        height: 0px;
      }

      &__top-fade {
        position: relative;
        height: 50px;
        background: linear-gradient(to bottom, rgba(251, 251, 251, 1) 0%, rgba(251, 251, 251, 0) 100%);
        z-index: 1;
      }
    }
  }
}

.research-label {
  color: var(--candy-wrapper-1000);
  font-weight: 400;
}

.research-icon {
  font-weight: 200;
}
</style>
